import { ActivityType } from '@/hooks/api/activities/types';
import { SimpleNote } from '@/hooks/api/patients/notes/types';
import { Picture } from '@/hooks/api/types';

import {
  OccurrenceResponse,
  OccurrenceStatus,
  Score,
  ScoreEvaluation,
} from '../assigned-activities/types';
import { ActivationTime } from '../journals/types';

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  AGENDER = 'AGENDER',
  NON_BINARY = 'NON_BINARY',
}

export enum Language {
  DE = 'DE',
  FR = 'FR',
  EN = 'EN',
}

export enum PatientStatus {
  FULLY_AVAILABLE = 'FULLY_AVAILABLE',
  AVAILABLE_FOR_MHP = 'AVAILABLE_FOR_MHP',
  UNAVAILABLE = 'UNAVAILABLE',
  IN_TRANSFER = 'IN_TRANSFER',
}

export interface AssignedPractitioner {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  photo?: Picture;
}

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  birthdate: string;
  age: number;
  pronouns?: string;
  /**
   * @deprecated Use `status` field with `PatientStatus.AVAILABLE_FOR_MHP` instead.
   *
   * @example
   * // Instead of:
   * if (patient.inactive) { ... }
   *
   * // Use:
   * if (patient.status === PatientStatus.AVAILABLE_FOR_MHP) { ... }
   */
  inactive: boolean;
  practitioners: Array<string>;
  assignedPractitionersLength?: number;
  sex?: string;
  gender?: Gender;
  latestLogin?: string;
  profileCreationDate?: string;
  activities?: Array<ActivityType>;
  notesAndReminders?: Array<SimpleNote>;
  photo?: Picture;
  status?: PatientStatus;
  session?: {
    sessionNumber: number;
    startDate: string;
  };
  groups?: Array<string>;
}

export interface PatientInvitation {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  email: string;
  birthdate: string;
  age: number;
  lastSendingDate: string;
  expired: boolean;
  photo?: Picture;
  groups?: Array<{ id: string; name: string }>;
}

export type ActivityPeriodKey =
  | 'EARLY_MORNING'
  | 'MORNING'
  | 'NOON'
  | 'AFTERNOON'
  | 'EVENING'
  | 'LATE_EVENING'
  | 'NIGHT'
  | 'ALL_DAY'
  | 'SITUATIONAL';

export interface ActivityPreference {
  startTime: string;
  startsNextDay: boolean;
  endTime: string;
  endsNextDay: boolean;
}

export type ActivityPreferenceConfig = Partial<
  Record<ActivityPeriodKey, ActivityPreference>
>;

export interface PatientDetails extends Patient {
  contactEmail?: string;
  contactPhoneNumber?: string;
  language?: Language;
  disabledDate?: string;
  isAssignedToMe?: boolean;
  isDiagnosisVisible: boolean;
  removeAccountRequestId?: string;
  activityPreferenceConfig?: ActivityPreferenceConfig;
}

export enum PatientStatusFilter {
  'INACTIVE' = 'INACTIVE',
  'ALL' = 'ALL',
}

export enum TherapyPlanType {
  SAFETY_PLAN = 'SAFETY_PLAN',
  TREATMENT_PLAN = 'TREATMENT_PLAN',
}

export enum TherapyPlanAccess {
  FULL_ACCESS = 'FULL_ACCESS',
  READ_ONLY = 'READ_ONLY',
  RESTRICTED = 'RESTRICTED',
}

export enum DefaultElementType {
  GOAL = 'GOAL',
  DIAGNOSIS = 'DIAGNOSIS',
  RESOURCE = 'RESOURCE',
  MENTAL_CHALLENGE = 'MENTAL_CHALLENGE',
  PREVENTION = 'PREVENTION',
  VULNERABILITY = 'VULNERABILITY',
  WARNING_SIGN = 'WARNING_SIGN',
  PERCEPTION_OF_OTHERS = 'PERCEPTION_OF_OTHERS',
  SKILL = 'SKILL',
  PERSONAL_CONTACT = 'PERSONAL_CONTACT',
  PROFESSIONAL_SUPPORT = 'PROFESSIONAL_SUPPORT',
  DETECTION_OF_EMERGENCY = 'DETECTION_OF_EMERGENCY',
  CHANGE_MOTIVATION = 'CHANGE_MOTIVATION',
  CLINICAL_HISTORY = 'CLINICAL_HISTORY',
  PSYCHOPATOLOGICAL_ASSESSMENT = 'PSYCHOPATOLOGICAL_ASSESSMENT',
  BIOGRAPHICAL_HISTORY = 'BIOGRAPHICAL_HISTORY',
  LIFE_SITUATION = 'LIFE_SITUATION',
  THERAPY_PLANNING_NOTES = 'THERAPY_PLANNING_NOTES',
  MOTIVATION_TO_LIVE = 'MOTIVATION_TO_LIVE',
  EMERGENCY_STRATEGY = 'EMERGENCY_STRATEGY',
  GLOBAL_ASSESSMENT = 'GLOBAL_ASSESSMENT',
  EXTERNAL_CONTACT = 'EXTERNAL_CONTACT',
}

export interface TherapyPlan {
  therapyType: TherapyPlanType;
  access: TherapyPlanAccess;
  isEnabled: boolean;
  order: number;
  defaultElementType: DefaultElementType;
}

export interface QuestionnaireInfo {
  id: string;
  title: string;
  picture?: Picture;
  scoreEvaluations?: Array<ScoreEvaluation>;
  hasMainScore: boolean;
}

export interface QuestionnaireResult {
  completedAt: string;
  mainScore: number;
}

export interface PatientQuestionnaireResults {
  id: string;
  assignedActivityId: string;
  assignedPathwayId?: string;
  title?: string;
  completedAt?: string;
  becomesActiveAt: string;
  expectedActiveAt?: string;
  duration?: number;
  status?: OccurrenceStatus;
  mainScore?: Score;
  responses?: Array<OccurrenceResponse>;
  scores?: Array<Score>;
  activationTime?: ActivationTime;
}

export interface TransferPatientResponse {
  transferUrl: string;
  createdAt?: Date;
  originalInstitutionId: string;
  status: 'STARTED';
  patientId: string;
  requestedBy: string;
  id: string;
}

export interface TransferPatientSettingsResponse {
  transferLinkExpiryDays: number;
}

export interface TransferUrlResponse {
  url: string;
  transferId: string;
}

export enum OverviewType {
  TREATMENT_CONTEXT = 'TREATMENT_CONTEXT',
  QUESTIONNAIRES = 'QUESTIONNAIRES',
  JOURNALS = 'JOURNALS',
  PATHWAYS = 'PATHWAYS',
  EXERCISES = 'EXERCISES',
}

export enum OverviewFilter {
  SHOW_ALL = 'SHOW_ALL',
  SHOW_ALL_SINCE_LAST_SESSION = 'SHOW_ALL_SINCE_LAST_SESSION',
  SHOW_ALL_FROM_LAST_TWO_WEEKS = 'SHOW_ALL_FROM_LAST_TWO_WEEKS',
  SHOW_ALL_FROM_LAST_MONTH = 'SHOW_ALL_FROM_LAST_MONTH',
  SHOW_ALL_FROM_LAST_THREE_MONTHS = 'SHOW_ALL_FROM_LAST_THREE_MONTHS',
}

export interface OverviewConfig {
  overviewType: OverviewType;
  order: number;
}

export interface OverviewSettings {
  elements: Array<OverviewConfig>;
  filters: OverviewFilter;
}
